import Logo from './Logo';

import React from 'react';
import { PhoneIcon, CheckCircleIcon, LockIcon, InfoIcon, BriefcaseIcon, StarIcon, HeartIcon, MoonIcon } from './Icons';

interface FooterProps {
    onNavigate: (page: string) => void;
    onOpenLegal?: () => void;
    onOpenSarah?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate, onOpenLegal, onOpenSarah }) => {
  return (
    <footer className="relative z-10 bg-mystic-900/80 backdrop-blur-lg py-12 border-t border-mystic-700 text-gray-400 text-sm mt-auto">
        
        <div className="container mx-auto px-4 grid md:grid-cols-3 gap-8">
            <div>
                <div className="mb-4">
                    <Logo size="md" showTagline={true} onClick={() => onNavigate('home')} />
                </div>
                <p className="mb-4 text-justify leading-relaxed text-gray-300">
                    Votre guide spirituel en ligne. Plus de 100 voyants sélectionnés pour leurs dons exceptionnels et leur éthique irréprochable. Entrez dans la lumière.
                </p>
                <p className="text-xs opacity-60 mb-4">© 2025 AstroX. Tous droits réservés.</p>
                
                {/* Mentions légales et Disclaimer */}
                <div className="flex flex-col gap-2 border-t border-white/10 pt-4">
                    {onOpenLegal && (
                        <button onClick={onOpenLegal} className="text-xs text-gold-400/90 hover:text-gold-300 transition-colors text-left underline decoration-gold-600/50 underline-offset-2 font-medium cursor-pointer">
                            Mentions Légales, CGU & Confidentialité
                        </button>
                    )}
                    <p className="text-[10px] text-gray-400 leading-relaxed text-left max-w-xs">
                        <strong className="text-gray-300">AstroX Digital Services SAS</strong> • SIRET 912 345 678 00014 • RCS Paris<br/>
                        Hébergement : Hostinger International Ltd. • Conforme RGPD & CNIL.<br/>
                        <span className="opacity-75">Service de divertissement réservé aux personnes majeures (+18 ans).</span>
                    </p>
                </div>
            </div>
            
            <div>
                <h5 className="text-white font-bold text-lg mb-4 border-l-4 border-gold-500 pl-3 uppercase tracking-widest text-xs">Navigation</h5>
                <ul className="space-y-3">
                    <li>
                        <button onClick={() => onNavigate('mediums')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Nos Médiums
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('horoscope')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Horoscope
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('love')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Astro Match
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('numerology')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Numérologie
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('dreams')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Rêves
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('crystal')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Boule de Cristal
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onOpenSarah ? onOpenSarah() : onNavigate('sarah')} className="hover:text-amber-300 transition-colors flex items-center gap-2 group font-bold text-amber-400">
                            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span> Assistance & Secrétariat (En direct)
                        </button>
                    </li>
                    <li>
                        <button onClick={() => onNavigate('tarifs')} className="hover:text-gold-400 transition-colors flex items-center gap-2 group">
                            <span className="w-1 h-1 bg-gray-500 rounded-full group-hover:bg-gold-400 transition-colors"></span> Contact & Tarifs
                        </button>
                    </li>
                </ul>
            </div>
            
            <div>
                <h5 className="text-white font-bold text-lg mb-4 border-l-4 border-purple-500 pl-3 uppercase tracking-widest text-xs">Nous contacter</h5>
                <p className="mb-2 font-bold text-white text-2xl font-serif tracking-wide">09 85 40 52 74</p>
                <p className="text-xs text-green-400 mb-4 font-medium flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    Appel non surtaxé • 08h-23h (Fermé Samedi)
                </p>
                <p className="mb-4 hover:text-white transition-colors">Email: contact@astrox.fr</p>
                
                <div className="p-4 bg-black/40 border border-mystic-600/50 rounded-lg text-xs leading-relaxed shadow-inner">
                    <strong className="block text-gold-400 mb-1 uppercase tracking-wide text-[10px]">Tarification Transparente</strong>
                    La consultation privée avec nos experts s'effectue sur un numéro surtaxé (08) au tarif de <strong>0,80€ / minute</strong>. Ce numéro vous est communiqué par notre secrétariat.
                    <br/><br/>
                    <span className="opacity-50">Service réservé aux majeurs.</span>
                </div>
            </div>
        </div>
        
        {/* Footer Bottom / Credits */}
        <div className="border-t border-white/5 mt-8 pt-6 pb-6 text-center flex flex-col sm:flex-row items-center justify-between px-4 container mx-auto gap-3">
            {/* Credits Signature */}
            <p className="text-[10px] text-gray-700 flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity">
                Design par <span className="text-gold-600/80 font-bold">MBA Support</span> 
            </p>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-mystic-500/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-900 via-gold-500/50 to-purple-900"></div>
    </footer>
  );
};

export default Footer;
